/**
 * Analytics state (screenshots/82–86): the dashboards you can open, the share directory and who you are, hydrated from
 * `snapshot:analytics` before first paint (main.tsx), plus every dashboard's title for the sidebar's starred list
 * (`snapshot:me` → `dashboards`, on every page). Calls the dashboards API (worker/src/dashboard-analytics.js); each
 * change updates the list and the sidebar titles right away.
 */
import { createStore } from "zustand/vanilla";
import { useStore } from "zustand";
import type { Dashboard, Principal } from "./dashboards";
import type { AnalyticsSnapshot, DashboardTitle } from "./types";
import { errorText } from "../lib/apiError";

export interface ShareBody {
  visibility?: Dashboard["visibility"];
  teamAccess?: Dashboard["teamAccess"];
  members?: Dashboard["members"];
  link?: { mode: Dashboard["link"]["mode"]; data?: Dashboard["link"]["data"]; expiresDays?: 7 | 30 | 90 | null; reset?: boolean; update?: boolean };
  schedule?: { on: boolean; to?: Principal[] } | null;
}
export type MetaPatch = Partial<Pick<Dashboard, "title" | "description" | "filters" | "print" | "themeId">>;

interface AnalyticsState {
  snapshot: AnalyticsSnapshot | null;
  dashboards: Dashboard[];
  titles: DashboardTitle[];
  hydrate: (s: AnalyticsSnapshot) => void;
  hydrateTitles: (t: DashboardTitle[]) => void;
  upsert: (d: Dashboard) => void;
  create: (body: { title: string; description?: string; start: { kind: "blank" | "copyOverview" | "template"; id?: string }; visibility: Dashboard["visibility"]; teamAccess: Dashboard["teamAccess"] }) => Promise<Dashboard>;
  duplicate: (id: string) => Promise<Dashboard>;
  saveMeta: (id: string, patch: MetaPatch) => Promise<Dashboard>;
  share: (id: string, body: ShareBody) => Promise<Dashboard>;
  remove: (id: string) => Promise<void>;
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, { method, credentials: "include", headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body) });
  const out = await r.json().catch(() => ({})) as T;
  if (!r.ok) throw Object.assign(new Error(errorText(out) || String(r.status)), { status: r.status });
  return out;
}

const titlesOf = (list: Dashboard[]): DashboardTitle[] => list.map((d) => ({ id: d.id, title: d.title }));

export const analyticsStore = createStore<AnalyticsState>()((set, get) => {
  const put = (d: Dashboard) => {
    const list = get().dashboards.some((x) => x.id === d.id) ? get().dashboards.map((x) => (x.id === d.id ? d : x)) : [d, ...get().dashboards];
    set({ dashboards: list, titles: titlesOf(list) });
  };
  return {
    snapshot: null, dashboards: [], titles: [],
    hydrate: (s) => set({ snapshot: s, dashboards: s.dashboards, titles: titlesOf(s.dashboards) }),
    hydrateTitles: (titles) => { if (!get().snapshot) set({ titles }); },
    upsert: put,
    create: async (body) => { const d = await call<Dashboard>("POST", "/api/dashboards", body); put(d); return d; },
    duplicate: async (id) => { const d = await call<Dashboard>("POST", `/api/dashboards/${encodeURIComponent(id)}/duplicate`); put(d); return d; },
    saveMeta: async (id, patch) => {
      const r = await call<{ dashboard: Dashboard }>("PUT", `/api/dashboards/${encodeURIComponent(id)}`, patch);
      put(r.dashboard);
      return r.dashboard;
    },
    share: async (id, body) => { const d = await call<Dashboard>("PUT", `/api/dashboards/${encodeURIComponent(id)}/share`, body); put(d); return d; },
    remove: async (id) => {
      await call("DELETE", `/api/dashboards/${encodeURIComponent(id)}`);
      const list = get().dashboards.filter((d) => d.id !== id);
      set({ dashboards: list, titles: titlesOf(list) });
    },
  };
});

export const useAnalytics = <T,>(sel: (s: AnalyticsState) => T) => useStore(analyticsStore, sel);
